import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import {
  FormSection,
  MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
  FilterConfig,
  MasterDataTable,
  TableColumn,
} from '../components/master-data/MasterDataTable';
import { City, Country, District, Pincode, State } from '../types/pincode';

import { Badge } from '@/components/ui/badge';
import { z } from 'zod';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const brandingPickupOptions = [
  { value: 'Available', label: 'Available' },
  { value: 'Unavailable', label: 'Unavailable' },
];

const PincodeManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPincode, setEditingPincode] = useState<Pincode | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch pincodes with server-side filtering and pagination
  const {
    data: pincodesResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'pincodes',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/pincode?${params.toString()}`
      );
      const result = response.data.data || {};
      console.log('Pincode API response:', result);

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const pincodes = pincodesResponse?.data || [];
  const pagination = pincodesResponse?.pagination;

  // Fetch countries for dropdown
  const { data: countriesData = [] } = useQuery({
    queryKey: ['countries-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/country');
      console.log('PincodeManagement - Countries API Response:', response.data);
      const countriesData =
        response.data.data?.data?.map((country: any) => ({
          value: country._id,
          label: `${country.name} (${country.code})`,
        })) || [];
      console.log('PincodeManagement - Countries for dropdown:', countriesData);
      return countriesData;
    },
  });

  // Fetch states for dropdown
  const { data: statesData = [] } = useQuery({
    queryKey: ['states-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/state');
      console.log('PincodeManagement - States API Response:', response.data);
      const statesData =
        response.data.data?.data?.map((state: any) => ({
          value: state._id,
          label: `${state.name} (${state.code})`,
          country: state.country,
        })) || [];
      console.log('PincodeManagement - States for dropdown:', statesData);
      return statesData;
    },
  });

  // Fetch districts for dropdown
  const { data: districtsData = [] } = useQuery({
    queryKey: ['districts-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/district');
      console.log('PincodeManagement - Districts API Response:', response.data);
      const districtsData =
        response.data.data?.data?.map((district: any) => ({
          value: district._id,
          label: `${district.name} (${district.code})`,
          state: district.state,
        })) || [];
      console.log('PincodeManagement - Districts for dropdown:', districtsData);
      return districtsData;
    },
  });

  // Fetch cities for dropdown
  const { data: citiesData = [] } = useQuery({
    queryKey: ['cities-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/city');
      console.log('PincodeManagement - Cities API Response:', response.data);
      const citiesData =
        response.data.data?.data?.map((city: any) => ({
          value: city._id,
          label: `${city.name} (${city.code})`,
          district: city.district,
        })) || [];
      console.log('PincodeManagement - Cities for dropdown:', citiesData);
      return citiesData;
    },
  });

  const countryOptions = countriesData;
  const stateOptions = statesData;
  const districtOptions = districtsData;
  const cityOptions = citiesData;

  // Show debugging info
  React.useEffect(() => {
    console.log('Pincodes API Response:', pincodesResponse);
    console.log('Pincodes data:', pincodes);
    console.log('Pincodes pagination:', pagination);
    console.log('Pincodes length:', pincodes.length);
    console.log('Is pincodes array?', Array.isArray(pincodes));
    console.log('Countries data:', countriesData);
    console.log('States data:', statesData);
    console.log('Districts data:', districtsData);
    console.log('Cities data:', citiesData);

    // Check individual pincode object
    if (pincodes.length > 0) {
      console.log('First pincode object:', pincodes[0]);
      console.log('Pincode keys:', Object.keys(pincodes[0]));
    }
  }, [
    pincodesResponse,
    pincodes,
    pagination,
    countriesData,
    statesData,
    districtsData,
    citiesData,
  ]);

  // Create pincode mutation
  const createPincodeMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/pincode', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pincodes'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'pincode'],
      });
      toast({
        title: 'Pincode created',
        description: 'Pincode has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingPincode(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error creating pincode',
          description:
            error.response?.data?.error?.message ||
            error.response?.data?.error ||
            'Failed to create pincode',
          variant: 'destructive',
        });
      }
    },
  });

  // Update pincode mutation
  const updatePincodeMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(`/api/master-data/pincode/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pincodes'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'pincode'],
      });
      toast({
        title: 'Pincode updated',
        description: 'Pincode has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingPincode(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error updating pincode',
          description:
            error.response?.data?.error?.message ||
            error.response?.data?.error ||
            'Failed to update pincode',
          variant: 'destructive',
        });
      }
    },
  });

  // Delete pincode mutation
  const deletePincodeMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/pincode/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pincodes'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'pincode'],
      });
      toast({
        title: 'Pincode deleted',
        description: 'Pincode has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error deleting pincode',
        description: error.response?.data?.error || 'Failed to delete pincode',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration with better responsive design
  const columns: TableColumn<Pincode>[] = [
    {
      key: 'pincode',
      label: 'Pincode',
      sortable: true,
      render: (value, pincode) => (
        <div className='space-y-1'>
          <div className='font-medium text-sm'>{pincode.pincode}</div>
          <div className='text-xs text-muted-foreground'>
            {typeof pincode.city === 'object' && (pincode.city as any)?.name
              ? (pincode.city as any).name
              : (pincode.city as string) || '-'}
          </div>
        </div>
      ),
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      hideOnTablet: true,
      render: (value, pincode) => (
        <div className='font-mono text-sm bg-muted px-2 py-1 rounded'>
          {pincode.code || '-'}
        </div>
      ),
    },
    {
      key: 'district',
      label: 'District',
      sortable: true,
      render: (value, pincode) => (
        <div className='space-y-1'>
          <div className='font-medium text-sm'>
            {typeof pincode.district === 'object'
              ? (pincode.district as District).name
              : pincode.district || '-'}
          </div>
          {typeof pincode.district === 'object' &&
            (pincode.district as District).code && (
              <div className='text-xs text-muted-foreground font-mono'>
                {(pincode.district as District).code}
              </div>
            )}
        </div>
      ),
    },
    {
      key: 'state',
      label: 'State',
      sortable: true,
      hideOnTablet: true,
      render: (value, pincode) => (
        <div className='space-y-1'>
          <div className='font-medium text-sm'>
            {typeof pincode.state === 'object'
              ? (pincode.state as State).name
              : pincode.state || '-'}
          </div>
          {typeof pincode.state === 'object' &&
            (pincode.state as State).code && (
              <div className='text-xs text-muted-foreground font-mono'>
                {(pincode.state as State).code}
              </div>
            )}
        </div>
      ),
    },
    {
      key: 'brandingPickupAvailable',
      label: 'Branding Pickup',
      sortable: true,
      render: (value, pincode) => (
        <Badge
          variant={
            pincode.brandingPickupAvailable === 'Available'
              ? 'default'
              : 'secondary'
          }
          className={
            pincode.brandingPickupAvailable === 'Available'
              ? 'bg-blue-100 text-blue-800 hover:bg-blue-100'
              : 'bg-gray-100 text-gray-800 hover:bg-gray-100'
          }
        >
          {pincode.brandingPickupAvailable}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, pincode) => (
        <Badge
          variant={pincode.status === 'Active' ? 'default' : 'secondary'}
          className={
            pincode.status === 'Active'
              ? 'bg-green-100 text-green-800 hover:bg-green-100'
              : 'bg-red-100 text-red-800 hover:bg-red-100'
          }
        >
          {pincode.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (value, pincode) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(pincode.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Enhanced filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [{ value: 'all', label: 'All Statuses' }, ...statusOptions],
      placeholder: 'Filter by status',
    },
    {
      key: 'brandingPickupAvailable',
      label: 'Branding Pickup',
      type: 'select',
      options: [
        { value: 'all', label: 'All Options' },
        ...brandingPickupOptions,
      ],
      placeholder: 'Filter by branding pickup',
    },
    {
      key: 'country',
      label: 'Country',
      type: 'select',
      options: [
        { value: 'all', label: 'All Countries' },
        ...(countriesData || []).map((country: any) => ({
          value: country.value,
          label: country.label,
        })),
      ],
      placeholder: 'Filter by country',
    },
    {
      key: 'state',
      label: 'State',
      type: 'select',
      options: [
        { value: 'all', label: 'All States' },
        ...(statesData || [])
          .filter((state: any) => {
            // Filter states by selected country if any
            if (currentFilters.country && currentFilters.country !== 'all') {
              return state.country === currentFilters.country;
            }
            return true;
          })
          .map((state: any) => ({
            value: state.value,
            label: state.label,
          })),
      ],
      placeholder: 'Filter by state',
    },
    {
      key: 'district',
      label: 'District',
      type: 'select',
      options: [
        { value: 'all', label: 'All Districts' },
        ...(districtsData || [])
          .filter((district: any) => {
            // Filter districts by selected state if any
            if (currentFilters.state && currentFilters.state !== 'all') {
              return district.state === currentFilters.state;
            }
            return true;
          })
          .map((district: any) => ({
            value: district.value,
            label: district.label,
          })),
      ],
      placeholder: 'Filter by district',
    },
  ];

  // Form sections configuration with better layout
  const formSections: FormSection[] = [
    {
      title: 'Pincode Information',
      description: 'Configure pincode details and settings.',
      fields: [
        {
          name: 'pincode',
          label: 'Pincode',
          type: 'text',
          required: true,
          placeholder: 'Enter pincode (e.g., "400001", "110001")',
          description: 'The postal/ZIP code for this location',
          validation: z
            .string()
            .min(4, 'Pincode must be at least 4 characters')
            .max(10, 'Pincode cannot exceed 10 characters')
            .regex(
              /^[0-9A-Z\s-]+$/,
              'Pincode can only contain numbers, uppercase letters, spaces, and hyphens'
            ),
        },
        {
          name: 'code',
          label: 'Pincode Code',
          type: 'text',
          required: false, // Code is optional
          placeholder: 'Enter pincode code (e.g., "PIN400001", "PIN110001")',
          description: 'A unique short code for this pincode (optional)',
          validation: z
            .string()
            .max(20, 'Code cannot exceed 20 characters')
            .regex(
              /^[A-Z0-9_-]*$/,
              'Code can only contain uppercase letters, numbers, underscores and hyphens'
            )
            .optional()
            .or(z.literal('')),
        },
        {
          name: 'city',
          label: 'City',
          type: 'text', // Changed from select to text
          required: false, // City is optional
          placeholder: 'Enter city name',
          description: 'The city this pincode belongs to (optional)',
        },
        {
          name: 'country',
          label: 'Country',
          type: 'select',
          required: true,
          options: (countriesData || []).map((country: any) => ({
            value: country.value,
            label: country.label,
          })),
          placeholder: 'Select country',
          description: 'The country this pincode belongs to',
        },
        {
          name: 'state',
          label: 'State',
          type: 'text', // Changed from select to text
          required: false, // State is optional
          placeholder: 'Enter state name',
          description: 'The state this pincode belongs to (optional)',
        },
        {
          name: 'district',
          label: 'District',
          type: 'text', // Changed from select to text
          required: false, // District is optional
          placeholder: 'Enter district name',
          description: 'The district this pincode belongs to (optional)',
        },
        {
          name: 'brandingPickupAvailable',
          label: 'Branding Pickup Available',
          type: 'radio',
          required: false, // Branding pickup is optional
          options: [
            {
              value: 'Available',
              label: 'Available - Branding pickup is available',
            },
            {
              value: 'Unavailable',
              label: 'Unavailable - Branding pickup is not available',
            },
          ],
          description:
            'Set whether branding pickup service is available for this pincode (optional)',
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' },
          ],
          description: 'Set the availability status for this pincode',
        },
      ],
    },
  ];

  const handleCreate = () => {
    setEditingPincode(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (pincode: Pincode) => {
    // Transform data for form editing
    const transformedPincode = {
      ...pincode,
      // City, state, district are now text fields - extract name if object, otherwise use string value
      city:
        typeof pincode.city === 'object' && pincode.city !== null
          ? (pincode.city as City).name
          : pincode.city || '',
      state:
        typeof pincode.state === 'object' && pincode.state !== null
          ? (pincode.state as State).name
          : pincode.state || '',
      district:
        typeof pincode.district === 'object' && pincode.district !== null
          ? (pincode.district as District).name
          : pincode.district || '',
      // Country is still a reference - extract ID
      country:
        typeof pincode.country === 'object'
          ? (pincode.country as Country)._id
          : pincode.country,
      // Ensure status and brandingPickupAvailable are properly set
      status: pincode.status,
      brandingPickupAvailable: pincode.brandingPickupAvailable,
    };
    setEditingPincode(transformedPincode);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (pincode: Pincode) => {
    const cityName =
      typeof pincode.city === 'object' && pincode.city?.name
        ? pincode.city.name
        : 'Unknown City';
    if (
      window.confirm(
        `Are you sure you want to permanently delete pincode "${pincode.pincode}" for ${cityName}? This action cannot be undone.`
      )
    ) {
      deletePincodeMutation.mutate(pincode._id);
    }
  };

  const handleSubmit = (data: any) => {
    // Transform form data for better API compatibility
    const transformedData = {
      ...data,
      // Ensure code is uppercase
      code: data.code?.toUpperCase(),
      // Ensure pincode is uppercase
      pincode: data.pincode?.toUpperCase(),
    };

    if (editingPincode) {
      updatePincodeMutation.mutate({
        id: editingPincode._id,
        data: transformedData,
      });
    } else {
      createPincodeMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingPincode(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['pincodes'] });
    toast({
      title: 'Data refreshed',
      description: 'Pincode data has been refreshed successfully.',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      // Add current search and filters to export
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/pincode/export?${params.toString()}`,
        {
          responseType: 'blob',
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `pincodes_${timestamp}.xlsx`);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      // Clean up and remove the link
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Export successful',
        description: 'Pincode data has been exported successfully.',
      });
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description:
          error.response?.data?.error || 'Failed to export pincode data',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            Pincode Management
          </h1>
          <p className='text-muted-foreground'>
            Manage pincode master data with comprehensive controls
          </p>
        </div>
      </div>

      <MasterDataTable<Pincode>
        title='Pincodes'
        description='Manage and configure pincode information'
        data={pincodes}
        columns={columns}
        isLoading={isLoading}
        // Pagination
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Search
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search pincodes by pincode, code, or city...'
        // Filtering
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        // Sorting
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        // Actions
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        // Permissions
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingPincode ? 'Edit Pincode' : 'Create Pincode'}
        sections={formSections}
        initialData={editingPincode}
        onSubmit={handleSubmit}
        isLoading={
          createPincodeMutation.isPending || updatePincodeMutation.isPending
        }
        mode={editingPincode ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default PincodeManagement;
